import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const SESSION_KEYS = ['hasSession', 'userType', 'username', 'companyName', 'isAdmin', 'displayName'];

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    SESSION_KEYS.forEach(k => localStorage.removeItem(k));
  }, []);

  // On mount: restore session from HttpOnly cookie + wire up 401 interceptor
  useEffect(() => {
    if (localStorage.getItem('hasSession')) {
      axios.post('/api/auth/refresh', {}, { withCredentials: true })
        .then(res => setAccessToken(res.data.token))
        .catch(() => SESSION_KEYS.forEach(k => localStorage.removeItem(k)))
        .finally(() => setAuthLoading(false));
    } else {
      setAuthLoading(false);
    }

    // 401 interceptor: silently refresh and retry the failed request.
    // If the refresh itself fails, clear the session and redirect to login.
    let isRefreshing = false;
    let failedQueue = [];

    const processQueue = (err, token = null) => {
      failedQueue.forEach(p => (err ? p.reject(err) : p.resolve(token)));
      failedQueue = [];
    };

    const id = axios.interceptors.response.use(
      res => res,
      async err => {
        const orig = err.config;

        // Don't intercept non-401s, retried requests, or auth-endpoint calls
        // (intercepting /api/auth/ would cause infinite refresh loops)
        if (
          err.response?.status !== 401 ||
          orig._retry ||
          orig.url?.includes('/api/auth/')
        ) {
          return Promise.reject(err);
        }

        // If a refresh is already in flight, queue this request to retry after
        if (isRefreshing) {
          return new Promise((resolve, reject) => failedQueue.push({ resolve, reject }))
            .then(token => {
              orig.headers['Authorization'] = `Bearer ${token}`;
              return axios(orig);
            });
        }

        orig._retry = true;
        isRefreshing = true;

        try {
          const res = await axios.post('/api/auth/refresh', {}, { withCredentials: true });
          const newToken = res.data.token;
          setAccessToken(newToken);
          processQueue(null, newToken);
          orig.headers['Authorization'] = `Bearer ${newToken}`;
          return axios(orig);
        } catch (refreshErr) {
          processQueue(refreshErr);
          clearSession();
          window.location.href = '/login';
          return Promise.reject(refreshErr);
        } finally {
          isRefreshing = false;
        }
      }
    );

    return () => axios.interceptors.response.eject(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Proactively refresh at 12 min so the access token never silently expires during use
  useEffect(() => {
    if (!accessToken) return;
    const timer = setTimeout(() => {
      axios.post('/api/auth/refresh', {}, { withCredentials: true })
        .then(res => setAccessToken(res.data.token))
        .catch(() => clearSession());
    }, 12 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [accessToken, clearSession]);

  return (
    <AuthContext.Provider value={{ accessToken, setAccessToken, clearSession, authLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
